import { useState, useEffect, Dispatch, SetStateAction } from "react";
import { AuthStorage } from "@/lib/storage";
import { User } from "@/types";

interface AuthSession {
    user: User | null;
    isLoading: boolean;
    setUser: Dispatch<SetStateAction<User | null>>;
    checkAuth: () => Promise<void>;
}

export const useAuthSession = (): AuthSession => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const checkAuth = async () => {
        try {
            // Priority 1: Check V2 Auth Endpoint
            const res = await fetch("/api/auth/me");
            if (res.ok) {
                const data = await res.json();
                if (data.authenticated && data.user) {
                    console.log("useAuthSession - Authenticated via V2 /api/auth/me", data.user);
                    setUser(data.user as User);
                    setIsLoading(false);
                    return;
                }
            }
        } catch (e) {
            console.warn("useAuthSession - V2 check failed, falling back to legacy", e);
        }

        // Priority 2: Legacy fallback (Cookies + V1 API)
        let userData = AuthStorage.getUser();
        console.log("useAuthSession - Legacy: Initial user data from cookies:", userData);

        // If no user data, wait a bit and try again (handles timing issues)
        if (!userData) {
            console.log("useAuthSession - Legacy: Retrying cookie read...");
            await new Promise((resolve) => setTimeout(resolve, 50));
            userData = AuthStorage.getUser();
        }

        // If still no user data, try server-side check as fallback
        if (!userData) {
            console.log("useAuthSession - Legacy: Trying /api/auth/check...");
            try {
                const response = await fetch("/api/auth/check", {
                    credentials: "include",
                });

                if (response.ok) {
                    const data = await response.json();
                    if (data.authenticated && data.user) {
                        userData = data.user;
                        console.log("useAuthSession - Legacy: Found user via /api/auth/check:", userData);
                    }
                }
            } catch (error) {
                console.error("useAuthSession - Legacy: Server check failed:", error);
            }
        }

        if (userData) {
            setUser(userData as User);
        } else {
            setUser(null);
        }
        setIsLoading(false);
    };

    useEffect(() => {
        // Initial auth check
        checkAuth();

        // Listen for auth changes
        const handleAuthChange = () => {
            checkAuth();
        };

        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === "user" || e.key === "auth_token") {
                checkAuth();
            }
        };

        window.addEventListener("auth-change", handleAuthChange);
        window.addEventListener("storage", handleStorageChange);

        return () => {
            window.removeEventListener("auth-change", handleAuthChange);
            window.removeEventListener("storage", handleStorageChange);
        };
    }, []);

    return { user, isLoading, setUser, checkAuth };
};
